import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AlertTriangle, ExternalLink, Lock, PauseCircle, RefreshCw, Save, Split, X, Ban, Undo2 } from 'lucide-react';
import type { BatchDetail, BatchTicketDetail, EvaluationView, Source } from '@autopilot/contracts';
import { api } from '../../api/client';
import { qk, useRoles } from '../../api/hooks';
import { ERROR_TEXT, describeError, isConflict } from '../../api/errors';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { StatusBadge, ToneBadge } from '../../components/StatusBadge';
import { ErrorAlert, LoadingState, FehlerZustand } from '../../components/States';
import { ANALYSIS_STATE_LABEL, EXECUTION_STATE_LABEL, RESOLUTION_KIND_LABEL, SOURCE_TYPE_LABEL, VORGANG_STATUS_LABEL } from '../../lib/labels';
import { formatDateTime } from '../../lib/format';
import { useIdempotencyKey } from '../../lib/idempotency';

export const CLAIM_RENEW_MS = 5 * 60 * 1000;
const VERLAUF_TYPES = new Set(['ANFRAGE', 'NACHRICHT', 'NOTIZ']);

export function needsNewReview(t: Pick<BatchTicketDetail, 'analysisState' | 'scoresCurrent' | 'proposalVersion'>): boolean {
  return t.analysisState === 'NEEDS_REVIEW' || (!t.scoresCurrent && t.proposalVersion !== null && t.analysisState !== 'EVALUATING');
}

function VerlaufItem({ s }: { s: Source }) {
  const intern = s.visibility === 'INTERN';
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        p: 1.5,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: intern ? 'var(--color-internal-line)' : 'divider',
        backgroundColor: intern ? 'var(--color-internal-soft)' : s.direction === 'AUSGEHEND' ? '#F7F8FA' : '#FFFFFF',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="subtitle2" component="span">
          {SOURCE_TYPE_LABEL[s.type]}
          {s.direction === 'EINGEHEND' ? ' · eingehend' : s.direction === 'AUSGEHEND' ? ' · ausgehend' : ''}
        </Typography>
        {intern && <Chip size="small" icon={<Lock size={12} />} label="Intern" sx={{ backgroundColor: '#FFEFBC', color: '#5C4A12', fontWeight: 700 }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {formatDateTime(s.effectiveAt)}
        </Typography>
      </Box>
      {s.title && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {s.title}
        </Typography>
      )}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {s.content ?? <em>Inhalt nicht verfügbar</em>}
      </Typography>
    </Box>
  );
}

function EvaluationCard({ title, ev }: { title: string; ev: EvaluationView | undefined }) {
  const status = ev?.status;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" component="h4">
            {title}
          </Typography>
          {status === 'VALID' && ev?.score !== null && (
            <Typography variant="h5" component="span" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
              {ev?.score}
            </Typography>
          )}
          {status === 'STALE' && <ToneBadge tone="warning" label={`veraltet${ev?.score != null ? ` (${ev.score})` : ''}`} sx={{ ml: 'auto' }} />}
          {status === 'PENDING' && <ToneBadge tone="info" label="wird geprüft" spinning sx={{ ml: 'auto' }} />}
          {status === 'FAILED' && <ToneBadge tone="error" label="Prüfung fehlgeschlagen" sx={{ ml: 'auto' }} />}
          {!ev && <ToneBadge tone="neutral" label="keine Bewertung" sx={{ ml: 'auto' }} />}
        </Box>
        {ev?.reason && (
          <Typography variant="body2" sx={{ color: status === 'STALE' ? 'text.secondary' : 'text.primary' }}>
            {ev.reason}
          </Typography>
        )}
        {ev && ev.blockers.length > 0 && (
          <Alert severity="error" icon={<Ban size={18} />} sx={{ mt: 1 }}>
            <strong>Sperre:</strong> {ev.blockers.join(' · ')}
          </Alert>
        )}
        {ev?.model && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            {ev.model.name} ({ev.model.modelId}) · bezogen auf Version {ev.proposalVersion}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function TicketModal({ ticketId, batch, onClose }: { ticketId: string | null; batch: BatchDetail; onClose: () => void }) {
  const open = !!ticketId;
  const titleId = useId();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { isOperator } = useRoles();
  const [draft, setDraft] = useState('');
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [claimLost, setClaimLost] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const evalIdem = useIdempotencyKey();
  const followIdem = useIdempotencyKey();
  const textRef = useRef<HTMLTextAreaElement>(null);

  const q = useQuery({
    queryKey: qk.ticket(ticketId ?? ''),
    queryFn: () => api.getTicket(ticketId!),
    enabled: open,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      const busy = ['EVALUATING', 'GENERATING', 'CONTEXT_LOADING', 'PENDING'].includes(d.analysisState);
      const exec = d.execution && ['QUEUED', 'SUBMITTING', 'PENDING_DELIVERY'].includes(d.execution.state);
      return busy || exec ? 2000 : false;
    },
  });
  const t = q.data;
  const current = t?.proposals[t.proposals.length - 1] ?? null;
  const dirty = baseVersion !== null && current !== null && draft !== (current.text ?? '');
  const editable = !!t && !t.readOnly && isOperator && !t.context?.expired && !claimLost;
  const scheduled = batch.trigger === 'SCHEDULED';

  // Entwurf übernehmen, wenn Ticket/Version wechselt und nichts ungespeichert ist
  useEffect(() => {
    if (!current) return;
    if (baseVersion === null || (!dirty && current.version !== baseVersion)) {
      setDraft(current.text ?? '');
      setBaseVersion(current.version);
    }
  }, [current, baseVersion, dirty]);

  useEffect(() => {
    if (!open) {
      setDraft('');
      setBaseVersion(null);
      setClaimLost(null);
      setClaimed(false);
      setNotice(null);
    }
  }, [open]);

  // Nach Abschluss einer Neubewertung Batch (Zähler, Tabelle, Verteilungen) aktualisieren
  const prevState = useRef<string | undefined>(undefined);
  useEffect(() => {
    const s = t?.analysisState;
    if (prevState.current === 'EVALUATING' && s && s !== 'EVALUATING') void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
    prevState.current = s;
  }, [t?.analysisState, qc, batch.id]);

  // Claim holen und alle 5 Minuten verlängern (08 §5)
  const canClaim = open && !!t && !t.readOnly && isOperator && !t.context?.expired;
  useEffect(() => {
    if (!canClaim || !ticketId) return;
    let cancelled = false;
    const take = async () => {
      try {
        await api.claim(ticketId);
        if (!cancelled) {
          setClaimed(true);
          setClaimLost(null);
        }
      } catch (err) {
        if (cancelled) return;
        setClaimed(false);
        const d = describeError(err);
        setClaimLost(
          d.code === 'MANUAL_CLAIM_ACTIVE'
            ? 'Dieses Ticket wird gerade von einer anderen Person geprüft. Sie können es ansehen, aber nicht ändern.'
            : `Die Bearbeitungssperre konnte nicht gehalten werden. ${d.message} Laden Sie das Ticket neu, bevor Sie weiterarbeiten.`,
        );
      }
    };
    void take();
    const iv = setInterval(() => void take(), CLAIM_RENEW_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
      api.releaseClaim(ticketId).catch(() => undefined);
    };
  }, [canClaim, ticketId]);

  // Browser-Schließen mit ungespeicherten Änderungen warnen
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const afterChange = (d?: BatchTicketDetail) => {
    if (d) qc.setQueryData(qk.ticket(d.id), d);
    void qc.invalidateQueries({ queryKey: qk.batch(batch.id) });
  };

  const save = useMutation({
    mutationFn: () => api.updateProposal(ticketId!, { expectedVersion: current!.version, text: draft }),
    onSuccess: (d) => {
      setBaseVersion(null);
      afterChange(d);
    },
    onError: (err) => {
      if (isConflict(err)) void q.refetch();
    },
  });
  const evaluate = useMutation({
    mutationFn: () => {
      const body = { expectedVersion: current!.version };
      return api.evaluate(ticketId!, body, evalIdem.keyFor({ ticketId, ...body }));
    },
    onSuccess: (d) => {
      evalIdem.reset();
      afterChange(d);
    },
  });
  const exclude = useMutation({
    mutationFn: () => api.setExclusion(ticketId!, { excluded: !t!.excluded, expectedRowVersion: t!.rowVersion }),
    onSuccess: () => {
      void q.refetch();
      afterChange();
    },
  });
  const followup = useMutation({
    mutationFn: () => api.followup(batch.id, [ticketId!], followIdem.keyFor({ batch: batch.id, ticketId })),
    onSuccess: (b) => {
      followIdem.reset();
      void qc.invalidateQueries({ queryKey: ['batches'] });
      onClose();
      nav(`/batches/${b.id}`);
    },
  });
  const hold = useMutation({
    mutationFn: () => api.createHold(t!.nidusTicketId, holdReason.trim()),
    onSuccess: () => {
      setHoldOpen(false);
      setHoldReason('');
      setNotice('Das Ticket ist für die Automatik zurückgestellt. Automatische Läufe bearbeiten diesen Inhaltsstand nicht, bis die Zurückstellung aufgehoben wird oder neuer Kontext vorliegt.');
    },
  });

  const requestClose = () => {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const verlauf = (t?.context?.sources ?? []).filter((s) => VERLAUF_TYPES.has(s.type)).sort((a, b) => (a.effectiveAt ?? '').localeCompare(b.effectiveAt ?? ''));
  const other = (t?.context?.sources ?? []).filter((s) => !VERLAUF_TYPES.has(s.type));
  const evCorr = t?.evaluations.filter((e) => e.role === 'CORRECTNESS').at(-1);
  const evRisk = t?.evaluations.filter((e) => e.role === 'ERROR_RISK').at(-1);
  const reviewNeeded = !!t && (needsNewReview(t) || dirty);
  const evaluating = t?.analysisState === 'EVALUATING';
  const anyError = save.error ?? evaluate.error ?? exclude.error ?? followup.error ?? hold.error;
  const errorAction = save.error ? 'Änderung speichern' : evaluate.error ? 'Erneut prüfen' : exclude.error ? 'Ausschluss ändern' : followup.error ? 'Folgebatch erstellen' : 'Zurückstellen';

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth="xl"
        fullWidth
        aria-labelledby={titleId}
        scroll="paper"
        slotProps={{ paper: { sx: { height: { md: 'calc(100% - 64px)' } } } }}
        data-testid="ticket-modal"
      >
        <DialogTitle id={titleId} sx={{ pr: 7, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <span>
              {t ? `${t.nummer}${t.betreff ? ` · ${t.betreff}` : ''}` : 'Ticket'}
            </span>
            {t?.nidusUrl && (
              <Link href={t.nidusUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}>
                In Nidus öffnen <ExternalLink size={14} aria-hidden />
                <span className="visually-hidden">(öffnet in neuem Tab)</span>
              </Link>
            )}
          </Box>
          {t && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusBadge status={t.currentStatus ?? t.status} />
              {t.currentStatus && t.currentStatus !== t.status && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  (bei Auswahl: {VORGANG_STATUS_LABEL[t.status]})
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t.gesellschaftName ?? '–'} · {t.objektBezeichnung ?? 'Ohne Liegenschaft'} · {t.vorgangstypName ?? 'Ohne Typ'}
              </Typography>
              {reviewNeeded && <ToneBadge tone="warning" label="Neue Prüfung erforderlich" data-testid="needs-review-badge" />}
              {evaluating && <ToneBadge tone="info" label="Wird geprüft" spinning />}
              {!reviewNeeded && !evaluating && t.analysisState !== 'READY' && (
                <ToneBadge tone={t.analysisState === 'FAILED' ? 'error' : 'neutral'} label={ANALYSIS_STATE_LABEL[t.analysisState]} />
              )}
              {t.excluded && <ToneBadge tone="neutral" label="Aus Batch ausgeschlossen" />}
              {claimed && <ToneBadge tone="brand" label="In Ihrer Bearbeitung" showIcon={false} />}
            </Box>
          )}
          <IconButton aria-label="Schließen" onClick={requestClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#FAFBFC' }}>
          {q.isPending ? (
            <LoadingState label="Ticket wird geladen …" />
          ) : q.isError ? (
            <FehlerZustand title="Ticket laden fehlgeschlagen" description={`${describeError(q.error).message} ${describeError(q.error).next}`} onRetry={() => void q.refetch()} />
          ) : t ? (
            <Box sx={{ display: 'grid', gap: 2.5 }}>
              {t.context?.expired && (
                <Alert severity="info">Inhalt abgelaufen: Die Texte dieses Tickets wurden gemäß Aufbewahrungsfrist gelöscht. Kennzahlen bleiben erhalten; eine erneute Ausführung ist nicht möglich.</Alert>
              )}
              {t.readOnly && t.readOnlyReason && (
                <Alert severity="info" icon={<Lock size={18} />} data-testid="readonly-reason">
                  {t.readOnlyReason}
                </Alert>
              )}
              {claimLost && (
                <Alert severity="warning" icon={<AlertTriangle size={20} />} data-testid="claim-lost">
                  {claimLost}
                </Alert>
              )}
              {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
              {t.execution && (
                <Alert severity={t.execution.state === 'UNKNOWN' || t.execution.state === 'FAILED' ? 'error' : t.execution.state === 'SUCCEEDED' ? 'success' : 'info'} icon={t.execution.state === 'UNKNOWN' ? <AlertTriangle size={20} /> : undefined}>
                  Ausführung: {EXECUTION_STATE_LABEL[t.execution.state].label}
                  {t.execution.agentDisplayName ? ` · Bearbeiter: ${t.execution.agentDisplayName}` : ''}
                  {t.execution.errorCode ? ` · ${ERROR_TEXT[t.execution.errorCode]?.message ?? 'Fehler bei der Ausführung.'}` : ''}
                  {t.execution.state === 'UNKNOWN' && ' Der Versand ist ungeklärt und muss in Nidus geklärt werden; es wird nichts automatisch wiederholt.'}
                </Alert>
              )}
              <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.15fr)' } }}>
                <Box component="section" aria-labelledby={`${titleId}-verlauf`}>
                  <Typography id={`${titleId}-verlauf`} variant="h6" component="h3" sx={{ mb: 1 }}>
                    Anfrage und Verlauf
                  </Typography>
                  {verlauf.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Kein Verlauf verfügbar.
                    </Typography>
                  ) : (
                    <Box component="ol" sx={{ p: 0, m: 0, display: 'grid', gap: 1.25 }}>
                      {verlauf.map((s) => (
                        <VerlaufItem key={s.sourceId} s={s} />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box component="section" aria-labelledby={`${titleId}-vorschlag`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography id={`${titleId}-vorschlag`} variant="h6" component="h3">
                      Antwortvorschlag
                    </Typography>
                    {current && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Version {current.version} · {current.author.kind === 'USER' ? `bearbeitet von ${current.author.name}` : `KI (${current.author.name})`}
                      </Typography>
                    )}
                    {current?.resolutionKind && (
                      <ToneBadge tone={current.resolutionKind === 'FINAL_ANSWER' ? 'success' : 'warning'} label={RESOLUTION_KIND_LABEL[current.resolutionKind]} sx={{ ml: 'auto' }} />
                    )}
                  </Box>
                  {current ? (
                    <TextField
                      inputRef={textRef}
                      multiline
                      minRows={14}
                      fullWidth
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      disabled={!editable}
                      label="Antworttext an den Melder"
                      helperText={
                        dirty
                          ? 'Ungespeicherte Änderung – nach dem Speichern ist eine neue Prüfung erforderlich.'
                          : editable
                            ? 'Änderungen erzeugen eine neue Version; beide bisherigen Bewertungen werden ungültig.'
                            : undefined
                      }
                      slotProps={{ input: { sx: { backgroundColor: '#fff', fontSize: '0.95rem', lineHeight: 1.6 } } }}
                      data-testid="proposal-text"
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Noch kein Vorschlag vorhanden{t.analysisState === 'FAILED' ? ' – die Verarbeitung ist fehlgeschlagen.' : '.'}
                    </Typography>
                  )}
                  {current && (current.missingInformation.length > 0 || current.blockers.length > 0) && (
                    <Alert severity="warning" icon={<AlertTriangle size={18} />} sx={{ mt: 1.5 }}>
                      {current.missingInformation.length > 0 && <div>Fehlende Informationen: {current.missingInformation.join(' · ')}</div>}
                      {current.blockers.length > 0 && <div>Sperren des Generators: {current.blockers.join(' · ')}</div>}
                    </Alert>
                  )}
                </Box>
              </Box>
              <Box component="section" aria-label="Prüfbegründungen" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <EvaluationCard title="Richtigkeit" ev={evCorr} />
                <EvaluationCard title="Fehlerrisiko" ev={evRisk} />
              </Box>
              {(t.context?.issues.length || t.context?.exclusionHits.length) ? (
                <Alert severity="warning" icon={<AlertTriangle size={18} />}>
                  {t.context?.exclusionHits.map((h, i) => (
                    <div key={`h${i}`}>Negativliste: Begriff „{h.term}“ gefunden in {h.sourceType.toLowerCase()}.</div>
                  ))}
                  {t.context?.issues.map((i, k) => (
                    <div key={`i${k}`}>
                      {i.required ? 'Pflichtquelle fehlt: ' : 'Hinweis: '}
                      {i.message}
                    </div>
                  ))}
                </Alert>
              ) : null}
              <Box component="section" aria-labelledby={`${titleId}-quellen`}>
                <Typography id={`${titleId}-quellen`} variant="h6" component="h3" sx={{ mb: 1 }}>
                  Weitere Quellen
                </Typography>
                {other.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Keine weiteren Quellen.
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Art</TableCell>
                          <TableCell>Titel</TableCell>
                          <TableCell>Sichtbarkeit</TableCell>
                          <TableCell>Stand</TableCell>
                          <TableCell>Gültig bis</TableCell>
                          <TableCell>Lesbarkeit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {other.map((s) => (
                          <TableRow key={s.sourceId}>
                            <TableCell>{SOURCE_TYPE_LABEL[s.type]}</TableCell>
                            <TableCell>{s.title ?? '–'}</TableCell>
                            <TableCell>{s.visibility === 'INTERN' ? 'Intern' : 'Extern'}</TableCell>
                            <TableCell>{formatDateTime(s.effectiveAt)}</TableCell>
                            <TableCell>{s.validityUnknown ? 'unbekannt' : s.validUntil ? formatDateTime(s.validUntil) : 'unbegrenzt'}</TableCell>
                            <TableCell>
                              {s.extractionStatus === 'FAILED' || s.extractionStatus === 'UNSUPPORTED' || s.extractionStatus === 'NOT_CLEAN' ? (
                                <ToneBadge tone="error" label="nicht lesbar" />
                              ) : s.extractionStatus === 'PENDING' ? (
                                <ToneBadge tone="info" label="ausstehend" />
                              ) : (
                                'ok'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
              {anyError ? <ErrorAlert error={anyError} action={errorAction} /> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          {t && scheduled && isOperator ? (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mr: 'auto' }}>
                Automatische Vorschläge sind schreibgeschützt.
              </Typography>
              <Button startIcon={<PauseCircle size={16} />} onClick={() => setHoldOpen(true)} disabled={hold.isPending}>
                Für Automatik zurückstellen
              </Button>
              <Button variant="contained" startIcon={<Split size={16} />} onClick={() => followup.mutate()} disabled={followup.isPending}>
                Manuellen Folgebatch erstellen
              </Button>
            </>
          ) : t && isOperator && !t.readOnly ? (
            <>
              <Button
                startIcon={t.excluded ? <Undo2 size={16} /> : <Ban size={16} />}
                onClick={() => exclude.mutate()}
                disabled={exclude.isPending || !!claimLost}
                sx={{ mr: 'auto' }}
                data-testid="exclude-ticket"
              >
                {t.excluded ? 'Wieder in Batch aufnehmen' : 'Aus Batch ausschließen'}
              </Button>
              <Button
                variant="outlined"
                startIcon={evaluate.isPending || evaluating ? <CircularProgress size={14} /> : <RefreshCw size={16} />}
                onClick={() => evaluate.mutate()}
                disabled={!editable || dirty || evaluate.isPending || evaluating || !current}
                title={dirty ? 'Bitte zuerst die Änderung speichern.' : undefined}
                data-testid="reevaluate"
              >
                Erneut prüfen
              </Button>
              <Button
                variant="contained"
                startIcon={save.isPending ? <CircularProgress size={14} color="inherit" /> : <Save size={16} />}
                onClick={() => save.mutate()}
                disabled={!editable || !dirty || save.isPending || !draft.trim()}
                data-testid="save-proposal"
              >
                Änderung speichern
              </Button>
            </>
          ) : (
            <Button onClick={requestClose}>Schließen</Button>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDiscard}
        title="Änderung verwerfen?"
        confirmLabel="Verwerfen"
        cancelLabel="Weiter bearbeiten"
        destructive
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          setDraft(current?.text ?? '');
          onClose();
        }}
      >
        Der geänderte Antworttext ist noch nicht gespeichert und geht beim Schließen verloren.
      </ConfirmDialog>
      <Dialog open={holdOpen} onClose={() => setHoldOpen(false)} aria-labelledby={`${titleId}-hold`} maxWidth="sm" fullWidth>
        <DialogTitle id={`${titleId}-hold`}>Für Automatik zurückstellen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Automatische Läufe bearbeiten diesen Ticket-Inhaltsstand nicht mehr, bis die Zurückstellung aufgehoben wird oder neuer fachlicher Kontext vorliegt. Manuelle Batches bleiben möglich.
          </Typography>
          <TextField label="Grund" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} fullWidth multiline minRows={2} required slotProps={{ htmlInput: { maxLength: 500 } }} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHoldOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => hold.mutate()} disabled={!holdReason.trim() || hold.isPending}>
            Zurückstellen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
